import { DialogActions, DialogContent } from '@material-ui/core';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Button } from '../common/Button';
import { DialogTitle } from '../common/Dialog';
import { TextFieldBoxed } from '../common/TextFieldBoxed';
import { Typography } from '../common/Typography';

export interface CreateFolderModalProps {
  onClose: () => void;
  createFolderAction: (name: string) => Promise<boolean>;
  onSuccess: () => void;
}

interface CreateFolderModalState {
  folderName: string;
  loading: boolean;
  error: string;
}

const initialState: CreateFolderModalState = {
  folderName: '',
  loading: false,
  error: '',
};

const getErrorText = (folderName: string, statusCode?: number) => {
  if (statusCode === 409) {
    return `The folder "${folderName}" could not be created, please check if a folder or file with this name already exists`;
  }
  return `The folder "${folderName}" could not be created`;
};

export const CreateFolderModal: React.FunctionComponent<CreateFolderModalProps> = ({
  onClose,
  createFolderAction,
  onSuccess,
}) => {
  const [{ folderName, loading, error }, setState] = useState(initialState);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setState((state) => ({ ...state, folderName: event.target.value }));
  };

  const handleOnSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setState((state) => ({ ...state, loading: true, error: '' }));

    try {
      const response = await createFolderAction(folderName);

      if (!response) {
        setState((state) => ({ ...state, error: getErrorText(folderName), loading: false }));
        return;
      }
    } catch (e: any) {
      console.error(e);
      setState((state) => ({
        ...state,
        error: getErrorText(folderName, e.status),
        loading: false,
      }));
      return;
    }

    setState((state) => ({ ...state, loading: false }));
    onClose();
    onSuccess();
  };

  return (
    <form onSubmit={handleOnSubmit}>
      <DialogTitle>Create New Folder</DialogTitle>
      <DialogContent>
        <TextFieldBoxed
          value={folderName}
          onChange={handleOnChange}
          autoFocus={true}
          required={true}
          id={'create-folder-textfield'}
          label={'Folder name'}
          InputLabelProps={{ required: false }}
          inputProps={{ maxLength: 50, min: 1 }}
          error={!!error}
          helperText={error}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose} disabled={loading}>
          <Typography variant='h5'>Cancel</Typography>
        </Button>
        <Button type='submit' disabled={loading}>
          <Typography variant='h5'>Create</Typography>
        </Button>
      </DialogActions>
    </form>
  );
};
